import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../../api/client";
import { qk } from "../../api/queries";
import type { TaskDetail, TaskPreview } from "../../api/types";
import { useDialogs } from "../Dialogs";
import { Icon } from "../Icon";
import { Markdown } from "../Markdown";
import { Pill } from "../Pill";
import { RelativeTime } from "../RelativeTime";
import { Spinner } from "../Spinner";

/** Opens a port of the task's environment through the preview proxy (one-time link). */
export function useOpenPreview(taskId: string) {
  const [busy, setBusy] = useState(false);
  const { toast } = useDialogs();
  const open = async (port: number, path?: string) => {
    // Open the window synchronously (popup blockers), then point it at the one-time URL.
    const win = window.open("about:blank", "_blank");
    if (win) win.opener = null;
    setBusy(true);
    try {
      const { url } = await api.previewLink(taskId, port, path);
      if (win) win.location.href = url;
      else window.location.href = url;
    } catch (e) {
      win?.close();
      toast(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return { open, busy };
}

/** Environment-level state shared by all of a task's previews. */
type EnvAvailability = "running" | "stopped" | "starting" | "gone";

function envAvailability(task: TaskDetail, envId: string): EnvAvailability {
  const env = task.environment;
  if (!env || env.id !== envId || env.state === "terminated" || env.state === "failed") return "gone";
  if (env.state === "stopped" || env.state === "stopping") return "stopped";
  if (env.state !== "running" || !env.connected) return "starting";
  return "running";
}

/** The agent's live previews (e.g. the app plus emulator UIs), shown first in the human review. */
export function LivePreviewCard({ task }: { task: TaskDetail }) {
  const qc = useQueryClient();
  const startEnv = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.startTaskEnvironment(task.id),
    onSettled: () => qc.invalidateQueries({ queryKey: qk.task(task.id) }),
  });
  const previews = task.previews ?? [];
  if (previews.length === 0) return null;
  const env = envAvailability(task, previews[0].env_id);

  return (
    <div className="review-block live-preview">
      <div className="live-preview-head">
        <h4>{previews.length === 1 ? "Live preview" : `Live previews · ${previews.length}`}</h4>
        {env === "stopped" && (
          <button className="btn btn-primary live-preview-open" onClick={() => startEnv.mutate()} disabled={startEnv.isPending}>
            <Icon name="play" size={14} /> {startEnv.isPending ? "Starting…" : "Start environment"}
          </button>
        )}
      </div>
      {env === "stopped" && (
        <p className="muted small live-preview-status">The environment was stopped while idle. Start it and the previews restart on their own.</p>
      )}
      {env === "starting" && (
        <p className="muted small live-preview-status">
          <Spinner /> The environment is starting; the previews restart with it.
        </p>
      )}
      {env === "gone" && <p className="muted small live-preview-status">The environment these previews ran in no longer exists.</p>}
      {startEnv.error && <p className="error-text small">{(startEnv.error as Error).message}</p>}
      <ul className="live-preview-list">
        {previews.map((p) => (
          <PreviewRow key={p.port} task={task} preview={p} env={env} />
        ))}
      </ul>
    </div>
  );
}

function PreviewRow({ task, preview: p, env }: { task: TaskDetail; preview: TaskPreview; env: EnvAvailability }) {
  const { open, busy } = useOpenPreview(task.id);
  const live = env === "running";
  return (
    <li className="live-preview-item">
      <div className="live-preview-main">
        <div className="live-preview-text">
          <div className="live-preview-title">
            <span>{p.label || `Port ${p.port}`}</span>
            {live && p.status === "ready" && <Pill color="green">Running</Pill>}
            {live && p.status === "starting" && <Pill color="yellow">Starting</Pill>}
            {live && p.status === "failed" && <Pill color="red">Failed</Pill>}
          </div>
          <div className="muted small">
            Port {p.port}
            {p.path !== "/" && <> · opens <code className="code-inline">{p.path}</code></>}
            {p.command && (
              <>
                {" · "}
                <code className="code-inline live-preview-cmd" title={p.command}>
                  {p.command}
                </code>
              </>
            )}
          </div>
        </div>
        <button
          className="btn btn-primary live-preview-open"
          onClick={() => open(p.port, p.path)}
          disabled={!live || p.status !== "ready" || busy}
          title={live && p.status === "ready" ? `Open port ${p.port}${p.path}` : undefined}
        >
          <Icon name="external" size={14} /> Open
        </button>
      </div>
      {live && p.status === "starting" && (
        <p className="muted small live-preview-status">
          <Spinner /> Building and starting (since <RelativeTime iso={p.updated_at} />). Progress is in the environment log on the Monitor page.
        </p>
      )}
      {live && p.status === "failed" && <pre className="live-preview-error">{p.error ?? "The preview did not start."}</pre>}
      {p.notes && (
        <div className="live-preview-notes">
          <Markdown>{p.notes}</Markdown>
        </div>
      )}
    </li>
  );
}
